"use client";

import { Skeleton } from "@offstreetparking/design-system/feedback";
import { CombinedParkingOptionSelection } from "@offstreetparking/portal-flow/components/CombinedParkingOptionSelection";
import { CreatePermitWizard } from "@offstreetparking/portal-flow/components/CreatePermitWizard";
import {
	PermitStoreProvider,
	useResumedEntrySelection,
} from "@offstreetparking/portal-flow/components/CreatePermitWizard/PermitContextProvider";
import { PermitTypeUnavailable } from "@offstreetparking/portal-flow/components/PermitTypeUnavailable";
import { SelectionLayout } from "@offstreetparking/portal-flow/components/SelectionLayout";
import { ParkingDetailsSkeleton } from "@offstreetparking/portal-flow/components/dataDisplay/parkingDetails";
import { ServerErrorAlert } from "@offstreetparking/portal-flow/components/forms/Alert";
import { ContactSkeleton } from "@offstreetparking/portal-flow/components/layouts/Contact";
import { ContentCard } from "@offstreetparking/portal-flow/components/layouts/ContentCard";
import { PermitFormLayout } from "@offstreetparking/portal-flow/components/layouts/PermitFormLayout";
import { useRouter, useSearchParams } from "next/navigation";
import { use, useEffect, useMemo, useState } from "react";
import { CommonLayout } from "~/components/layouts/CommonLayout";
import { useParker } from "~/components/providers/AuthProvider";
import { CompanyScope } from "~/components/providers/CompanyProvider";
import { useFeatureFlags } from "~/hooks/useFeatureFlags";
import { offstreetClient } from "~/services/offstreetApi";
import { usePortalV2LocationByIdentifier } from "~/services/portalV2Api";
import { normalizeRegSettings } from "~/utils/normalize-reg-settings";
import { redirectToPortalV2 } from "~/utils/redirectToP2";

type LocationTenantPageProps = {
	params: Promise<{ locale: string; locationIdentifier: string; tenantId: string }>;
};

export default function LocationTenantPage(props: LocationTenantPageProps) {
	const params = use(props.params);
	const { locationIdentifier, tenantId, locale } = params;
	const parsedTenantId = Number(tenantId);
	const isTenantIdValid = Number.isFinite(parsedTenantId);
	const resumedEntrySelection = useResumedEntrySelection();
	const [selectedPermitTypeId, setSelectedPermitTypeId] = useState(resumedEntrySelection?.permitTypeId);
	const searchParams = useSearchParams();
	const router = useRouter();
	const isTenantEditable = searchParams.get("tenantEditable") === "1";
	const { isPending: flagsPending } = useFeatureFlags();
	const { isInitialLoading: isParkerInitialLoading, parker } = useParker();

	const { data: p2Location, isPending: p2Pending } = usePortalV2LocationByIdentifier({
		identifier: locationIdentifier,
	});

	const {
		data: p3Data,
		isPending: p3Pending,
		error: p3Error,
	} = offstreetClient.locationLookup.getPermitTypesForLocation.useQuery(
		p2Location?.id
			? { locationId: p2Location.id, tenantId: parsedTenantId, permitTypeId: selectedPermitTypeId }
			: { identifier: locationIdentifier, tenantId: parsedTenantId, permitTypeId: selectedPermitTypeId },
		{ enabled: isTenantIdValid && (!!p2Location?.id || !p2Pending) },
	);

	const location = p3Data?.location ?? (p2Location ? { id: p2Location.id, name: p2Location.name } : undefined);
	const permitTypeInstructions = p3Data?.permitTypeInstructions?.registrationInstructions;
	const tenantInstructions = p3Data?.tenantInstructions?.registrationInstructions;
	const locationInstructions = p3Data?.locationInstructions?.registrationInstructions;

	const instructions =
		[permitTypeInstructions, tenantInstructions, locationInstructions].map((v) => v?.trim()).find((v) => v) ??
		undefined;

	const { data: selectedPermitType, isPending: wizardPending } =
		offstreetClient.locationLookup.getPermitTypeById.useQuery(
			{ permitTypeId: selectedPermitTypeId!, locationId: location?.id, tenantId: parsedTenantId },
			{ enabled: !!selectedPermitTypeId },
		);

	const p2Tenant = p2Location?.tenants.find((tenant) => tenant.id === parsedTenantId);
	const normalizedRegSettings = p2Location
		? normalizeRegSettings({
				settings: p2Location.registrationSettings,
				tenantRegSettingIds: p2Tenant?.registrationSettings ?? [],
				disabledSettings: p2Tenant?.disabledSettings,
			})
		: [];
	const permitTypes = p3Data?.permitTypes ?? [];
	const totalPermitTypeAndRegSettingDisplayed = permitTypes.length + normalizedRegSettings.length;
	const formData = useMemo(() => {
		if (!parker) {
			return undefined;
		}
		return {
			vehicles: parker.defaultVehicle ? [parker.defaultVehicle] : [],
			email: parker.email ?? undefined,
		};
	}, [parker]);

	// Auto-select when there's exactly 1 available option (permit type or reg setting)
	const autoSelectAction = (() => {
		if (p3Pending || p2Pending || selectedPermitTypeId) {
			return undefined;
		}
		const availableRegSettings = normalizedRegSettings.filter((s) => s.isAvailable && !s.isAtCapacity);
		const activePermitTypes = permitTypes.filter((pt) => pt.status === "active");
		const totalAvailable = activePermitTypes.length + availableRegSettings.length;
		if (totalPermitTypeAndRegSettingDisplayed !== 1 || totalAvailable !== 1) {
			return undefined;
		}
		if (activePermitTypes.length === 1) {
			return { type: "permitType" as const, id: activePermitTypes[0]!.id };
		}
		if (availableRegSettings.length === 1) {
			return { type: "regSetting" as const, id: availableRegSettings[0]!.id };
		}
		return undefined;
	})();

	useEffect(() => {
		if (!autoSelectAction) {
			return;
		}
		if (autoSelectAction.type === "permitType") {
			setSelectedPermitTypeId(autoSelectAction.id);
		} else {
			redirectToPortalV2({ path: `${locationIdentifier}/${tenantId}`, settingId: autoSelectAction.id });
		}
	}, [autoSelectAction, locationIdentifier, tenantId]);

	const isLoading = flagsPending || p2Pending || p3Pending || isParkerInitialLoading;

	if (isLoading) {
		return (
			<CommonLayout
				infoSlot={
					<div className="space-y-12">
						<div className="space-y-4">
							<Skeleton height={60} width={450} />
							<Skeleton height={60} width={225} />
						</div>

						<ContactSkeleton />
					</div>
				}
				isLoading={true}
			>
				<ContentCard>
					<ParkingDetailsSkeleton />
					<div className="border-neutral-faded border-t p-8 xl:hidden">
						<ContactSkeleton />
					</div>
				</ContentCard>
			</CommonLayout>
		);
	}

	if (p3Error) {
		return (
			<CommonLayout>
				<ServerErrorAlert serverError={p3Error} />
			</CommonLayout>
		);
	}

	const p3Tenant = p3Data?.tenants?.find((t) => t.id === parsedTenantId);
	const tenantName = p2Tenant?.name ?? p3Tenant?.name;
	const selectedTenantDisplay = tenantName
		? { name: tenantName, brandingFileUrl: p3Tenant?.brandingFileUrl }
		: undefined;

	if (autoSelectAction && !selectedPermitTypeId) {
		return null;
	}

	if (selectedPermitTypeId) {
		if (wizardPending) {
			return (
				<CommonLayout
					infoSlot={
						<div className="space-y-12">
							<div className="space-y-4">
								<Skeleton height={60} width={450} />
								<Skeleton height={60} width={225} />
							</div>

							<ContactSkeleton />
						</div>
					}
					isLoading={true}
				>
					<ContentCard>
						<ParkingDetailsSkeleton />
						<div className="border-neutral-faded border-t p-8 xl:hidden">
							<ContactSkeleton />
						</div>
					</ContentCard>
				</CommonLayout>
			);
		}

		if (selectedPermitType) {
			return (
				<PermitStoreProvider
					initialValues={{
						formData,
						wizardSettings: selectedPermitType.wizardSettings,
						entrySelection: { permitTypeId: selectedPermitTypeId },
					}}
				>
					{p3Data?.company && <CompanyScope company={p3Data.company} />}
					<PermitFormLayout
						name={selectedTenantDisplay?.name ?? selectedPermitType.name}
						branding={selectedPermitType.branding}
						companyName={selectedPermitType.company.name}
						whiteLabel={selectedPermitType.whiteLabel}
						support={selectedPermitType.company.support}
						contact={selectedPermitType.contact}
						locationId={locationIdentifier}
						location={location}
						selectedTenant={selectedTenantDisplay}
						selectedPermitType={{ name: selectedPermitType.name, isEvent: selectedPermitType.isEvent }}
						brandingUrl={selectedPermitType.brandingUrl}
						venue={selectedPermitType.venue}
						registrationInstructions={instructions ?? undefined}
						onChangeTenant={
							isTenantEditable ? () => router.push(`/${locale}/${locationIdentifier}`) : undefined
						}
						onChangePermitType={
							totalPermitTypeAndRegSettingDisplayed > 1
								? () => setSelectedPermitTypeId(undefined)
								: undefined
						}
					>
						{selectedPermitType.status !== "active" ? (
							<PermitTypeUnavailable
								status={selectedPermitType.status}
								choices={selectedPermitType.wizardSettings.choices}
							/>
						) : (
							<CreatePermitWizard
								permitType={selectedPermitType}
								preSelectedLocationId={location?.id}
								tenantId={parsedTenantId}
								registrationInstructions={instructions ?? undefined}
							/>
						)}
					</PermitFormLayout>
				</PermitStoreProvider>
			);
		}
	}

	return (
		<SelectionLayout
			location={location}
			selectedTenant={selectedTenantDisplay}
			branding={p3Data?.branding}
			support={p3Data?.support}
			contact={p3Data?.contact}
			registrationInstructions={instructions ?? undefined}
			onChangeTenant={isTenantEditable ? () => router.push(`/${locale}/${locationIdentifier}`) : undefined}
		>
			{p3Data?.company && <CompanyScope company={p3Data.company} />}
			<CombinedParkingOptionSelection
				permitTypes={permitTypes}
				timezone={p3Data?.location?.timezone}
				regSettings={normalizedRegSettings}
				onSelectPermitType={(id) => setSelectedPermitTypeId(id)}
				onSelectRegSetting={(settingId) =>
					redirectToPortalV2({ path: `${locationIdentifier}/${tenantId}`, settingId })
				}
			/>
		</SelectionLayout>
	);
}
